import macro from '@kitware/vtk.js/macro';
import type { Vector3 } from '@kitware/vtk.js/types';

/** Screen-space hit radius (CSS pixels) for picking existing handles. */
const HIT_RADIUS_PX = 12;

export interface CurvedMPRBehaviorCallbacks {
  /** Returns the current control point list (world LPS) for picking. */
  getPoints(): Vector3[];
  /** Returns true if the widget should accept input (active arch, etc.). */
  isInputEnabled(): boolean;
  /** Append a new point at the end of the curve. */
  addPoint(world: Vector3): void;
  /** Move the point at `index` to a new world position. */
  movePoint(index: number, world: Vector3): void;
  /** Remove the point at `index`. */
  removePoint(index: number): void;
}

export default function widgetBehavior(publicAPI: any, model: any) {
  model.classHierarchy.push('vtkCurvedMPRWidgetProp');

  macro.setGet(publicAPI, model, ['manipulator', 'callbacks']);

  function getCallbacks(): CurvedMPRBehaviorCallbacks | null {
    return model.callbacks ?? null;
  }

  /**
   * Pick the nearest handle in display space. World-mm distance fails when
   * control points sit off the current axial slice plane (they still look
   * correct on screen after scrolling).
   */
  function pickHandle(callData: any): number {
    const cb = getCallbacks();
    const rw = model._apiSpecificRenderWindow;
    const renderer = model._renderer;
    if (!cb || !rw || !renderer || !callData?.position) return -1;

    const clickX = callData.position.x;
    const clickY = callData.position.y;
    const dpr = rw.getComputedDevicePixelRatio?.() ?? 1;
    const hitRadiusSq = (HIT_RADIUS_PX * dpr) ** 2;

    const points = cb.getPoints();
    let best = -1;
    let bestDist = hitRadiusSq;

    for (let i = 0; i < points.length; i++) {
      const ptDisplay = rw.worldToDisplay(
        points[i][0],
        points[i][1],
        points[i][2],
        renderer
      );
      if (!ptDisplay) continue;
      const dx = ptDisplay[0] - clickX;
      const dy = ptDisplay[1] - clickY;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDist) {
        best = i;
        bestDist = d2;
      }
    }
    return best;
  }

  function resolveWorld(callData: any): Vector3 | null {
    if (!model.manipulator) return null;
    const { worldCoords } = model.manipulator.handleEvent(
      callData,
      model._apiSpecificRenderWindow
    );
    if (!worldCoords) return null;
    return worldCoords as Vector3;
  }

  publicAPI.handleLeftButtonPress = (callData: any) => {
    if (!model.pickable) return macro.VOID;
    const cb = getCallbacks();
    if (!cb || !cb.isInputEnabled()) return macro.VOID;
    const world = resolveWorld(callData);
    if (!world) return macro.VOID;

    const pickedIndex = pickHandle(callData);
    if (pickedIndex >= 0) {
      model.widgetState.setActiveIndex(pickedIndex);
      model.widgetState.setDragging(true);
      model._interactor.requestAnimation(publicAPI);
      model._apiSpecificRenderWindow.setCursor('grabbing');
      publicAPI.invokeStartInteractionEvent();
      return macro.EVENT_ABORT;
    }

    // Empty space: append a new control point.
    cb.addPoint([world[0], world[1], world[2]]);
    publicAPI.invokeInteractionEvent();
    return macro.EVENT_ABORT;
  };

  publicAPI.handleMouseMove = (callData: any) => {
    if (!model.widgetState.getDragging()) return macro.VOID;
    const cb = getCallbacks();
    if (!cb || !cb.isInputEnabled()) return macro.VOID;
    const activeIndex = model.widgetState.getActiveIndex();
    if (activeIndex < 0) return macro.VOID;

    const world = resolveWorld(callData);
    if (!world) return macro.VOID;
    cb.movePoint(activeIndex, [world[0], world[1], world[2]]);
    publicAPI.invokeInteractionEvent();
    return macro.EVENT_ABORT;
  };

  publicAPI.handleLeftButtonRelease = () => {
    if (!model.widgetState.getDragging()) return macro.VOID;
    model._interactor.cancelAnimation(publicAPI);
    model._apiSpecificRenderWindow.setCursor('default');
    model.widgetState.setDragging(false);
    model.widgetState.setActiveIndex(-1);
    publicAPI.invokeEndInteractionEvent();
    return macro.EVENT_ABORT;
  };

  publicAPI.handleRightButtonPress = (callData: any) => {
    if (!model.pickable) return macro.VOID;
    const cb = getCallbacks();
    if (!cb || !cb.isInputEnabled()) return macro.VOID;
    const world = resolveWorld(callData);
    if (!world) return macro.VOID;
    const pickedIndex = pickHandle(callData);
    if (pickedIndex >= 0) {
      cb.removePoint(pickedIndex);
      publicAPI.invokeInteractionEvent();
      return macro.EVENT_ABORT;
    }
    return macro.VOID;
  };

  publicAPI.grabFocus = () => {};
  publicAPI.loseFocus = () => {};
}
